import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Skeleton from '~/components/Skeleton'

const SkeletonPage: Component = () => {
  const [animated, setAnimated] = createSignal(true)
  const [shimmer, setShimmer] = createSignal(true)

  return (
    <>
      <Title>Skeleton - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Skeleton</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistema de carga progresiva. El componente Skeleton mitiga la percepción de latencia mediante placeholders que replican la estructura final del contenido, proporcionando una experiencia
            visual fluida.
          </p>
        </div>

        <div class="space-y-12">
          {/* Control de Animación */}
          <div class="flex flex-wrap gap-4 items-center p-6 bg-surface-muted rounded-3xl border border-border">
            <Button
              variant={animated() ? 'contained' : 'soft'}
              size="sm"
              color="primary"
              onClick={() => setAnimated(!animated())}
              icon={animated() ? 'material-symbols-light:play-arrow-outline' : 'material-symbols-light:stop-outline'}
            >
              Pulse: {animated() ? 'ON' : 'OFF'}
            </Button>
            <Button
              variant={shimmer() ? 'contained' : 'soft'}
              size="sm"
              color="secondary"
              onClick={() => setShimmer(!shimmer())}
              icon={shimmer() ? 'material-symbols-light:bolt-outline' : 'material-symbols-light:bolt-outline'}
            >
              Shimmer: {shimmer() ? 'ON' : 'OFF'}
            </Button>
            <div class="h-8 w-px bg-surface-muted mx-2 hidden sm:block"></div>
            <p class="text-xs text-foreground-subtle font-medium italic">Configura el comportamiento del placeholder para tu UI.</p>
          </div>

          {/* Variantes Atómicas */}
          <Card>
            <Card.Header
              title="Librería de Componentes"
              subtitle="Representaciones estructurales para los elementos básicos del sistema."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-8 p-6">
                <div class="space-y-3">
                  <Badge size="sm" color="info" variant="soft">
                    Line
                  </Badge>
                  <Skeleton variant="line" width="90%" animated={animated()} shimmer={shimmer()} />
                  <Skeleton variant="line" width="60%" animated={animated()} shimmer={shimmer()} />
                </div>
                <div class="space-y-3">
                  <Badge size="sm" color="secondary" variant="soft">
                    Avatar
                  </Badge>
                  <div class="flex items-center gap-3">
                    <Skeleton variant="avatar" animated={animated()} shimmer={shimmer()} />
                    <div class="space-y-1 w-full">
                      <Skeleton variant="line" width="100%" animated={animated()} shimmer={shimmer()} />
                      <Skeleton variant="line" width="60%" animated={animated()} shimmer={shimmer()} />
                    </div>
                  </div>
                </div>
                <div class="space-y-3">
                  <Badge size="sm" color="warning" variant="soft">
                    Image
                  </Badge>
                  <Skeleton variant="image" height={80} animated={animated()} shimmer={shimmer()} />
                </div>
                <div class="space-y-3">
                  <Badge size="sm" color="success" variant="soft">
                    Inputs
                  </Badge>
                  <Skeleton variant="input" animated={animated()} shimmer={shimmer()} />
                  <Skeleton variant="button" width="100%" animated={animated()} shimmer={shimmer()} />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Caso de Uso: Card de Perfil */}
          <Card>
            <Card.Header
              title="Mockup Estructural"
              subtitle="Ejemplo de composición compleja simulando una carga de perfil de usuario."
              icon={<Icon icon="material-symbols-light:account-circle-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-md mx-auto p-8 rounded-3xl border border-border bg-surface shadow-xl shadow-slate-200/50">
                <div class="flex items-center gap-6 mb-10">
                  <Skeleton variant="avatar" width={80} height={80} animated={animated()} shimmer={shimmer()} />
                  <div class="space-y-3 flex-1">
                    <Skeleton variant="line" height={24} width="80%" animated={animated()} shimmer={shimmer()} />
                    <Skeleton variant="line" width="40%" animated={animated()} shimmer={shimmer()} />
                  </div>
                </div>
                <div class="space-y-4 mb-10">
                  <Skeleton variant="line" width="100%" animated={animated()} shimmer={shimmer()} />
                  <Skeleton variant="line" width="100%" animated={animated()} shimmer={shimmer()} />
                  <Skeleton variant="line" width="70%" animated={animated()} shimmer={shimmer()} />
                </div>
                <div class="flex gap-4">
                  <Skeleton variant="button" class="flex-1" animated={animated()} shimmer={shimmer()} />
                  <Skeleton variant="button" class="flex-1" animated={animated()} shimmer={shimmer()} />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Skeleton."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">variant</td>
                      <td class="px-6 py-4 italic text-xs">'line' | 'image' | 'avatar' | 'button' | 'input' | 'card' | 'table'</td>
                      <td class="px-6 py-4 font-mono">'line'</td>
                      <td class="px-6 py-4 italic text-balance">Estructura predefinida del placeholder.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">animated</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">true</td>
                      <td class="px-6 py-4">Habilita la animación de 'pulse'.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">shimmer</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">true</td>
                      <td class="px-6 py-4 italic">Habilita el efecto de barrido de luz.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">circle</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Fuerza el redondeado total (border-radius: 9999px).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SkeletonPage
